import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import ProtectedRoute from "./components/ProtectedRoute";
import AppHeader from "./components/AppHeader";
import SiteNoticeBar from "./components/SiteNoticeBar";
import AdminRoute from "./components/AdminRoute";
import ScrollToTop from "./components/ScrollToTop";
import MainLayout from "./components/MainLayout";
import FeedbackPrompt from "./components/FeedbackPrompt";
import RouteSeo from "./components/RouteSeo";
import { lazy, Suspense, useEffect } from "react";
import { hideAppSplash } from "./utils/appSplash";
import { usePageTracking } from "./hooks/usePageTracking";

const LandingGate = lazy(() => import("./components/LandingGate"));
const UserHome = lazy(() => import("./pages/UserHome"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const MyResumes = lazy(() => import("./pages/MyResumes"));
const AIAssistant = lazy(() => import("./pages/AIAssistant"));
const Templates = lazy(() => import("./pages/Templates"));
// Showcase registry renderer — single component for all showcase CV routes
const ShowcaseCV = lazy(() => import("./pages/ShowcaseCV"));
const CVTemplatePreview = lazy(() => import("./pages/CVTemplatePreview"));
const CVBuilder = lazy(() => import("./pages/CVBuilder"));
const AICVBuilder = lazy(() => import("./pages/AICVBuilder"));
const Profile = lazy(() => import("./pages/Profile"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const BlogList = lazy(() => import("./pages/blog/BlogList"));
const SinglePost = lazy(() => import("./pages/blog/SinglePost"));
const CreatePost = lazy(() => import("./pages/admin/CreatePost"));
const EditPost = lazy(() => import("./pages/admin/EditPost"));
const Feedback = lazy(() => import("./pages/Feedback"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const TwoFactorVerify = lazy(() => import("./pages/TwoFactorVerify"));
const FeedbackManagement = lazy(
  () => import("./pages/admin/FeedbackManagement"),
);
const UserCvsPage = lazy(() => import("./pages/admin/UserCvsPage"));
const FinancePage = lazy(() => import("./pages/admin/FinancePage"));
const AiLogsPage = lazy(() => import("./pages/admin/AiLogsPage"));
const TemplatesManagement = lazy(
  () => import("./pages/admin/TemplatesManagement"),
);
const PricingManagement = lazy(
  () => import("./pages/admin/PricingManagement"),
);
const NewsletterPage = lazy(() => import("./pages/admin/NewsletterPage"));
const OffersPage = lazy(() => import("./pages/admin/OffersPage"));
const NewsletterUnsubscribe = lazy(
  () => import("./pages/NewsletterUnsubscribe"),
);
const CoverLetter = lazy(() => import("./pages/CoverLetter"));
const Pricing = lazy(() => import("./pages/Pricing"));
const BillingReturn = lazy(() => import("./pages/BillingReturn"));
const PortfolioProfile = lazy(() => import("./pages/PortfolioProfile"));
const NotFound = lazy(() => import("./pages/NotFound"));

function PublicLayout() {
  return <Outlet />;
}

function ProtectedLayout() {
  return (
    <ProtectedRoute>
      <Outlet />
    </ProtectedRoute>
  );
}

function CvBuilderAliasRedirect() {
  const location = useLocation();

  return (
    <Navigate
      to={{
        pathname: "/editor",
        search: location.search,
        hash: location.hash,
      }}
      replace
      state={location.state}
    />
  );
}

/**
 * Dismisses the first-paint splash for every route except the landing page.
 *
 * It sits inside the Suspense boundary as a sibling of <Routes>: React holds
 * back the whole boundary while a lazy route chunk loads, so this only mounts
 * once there is real page content to reveal. The landing page is excluded
 * because it has its own logo loader and dismisses the splash itself, at the
 * point where the two are showing the same thing.
 */
function SplashHandoff() {
  const location = useLocation();

  useEffect(() => {
    if (location.pathname === "/") return;
    hideAppSplash();
  }, [location.pathname]);

  return null;
}

function App() {
  usePageTracking();

  return (
    <div className="min-h-screen">
      <RouteSeo />
      <SiteNoticeBar />
      <AppHeader />
      <MainLayout>
        <ScrollToTop />
        <Suspense
          fallback={
            <div className="flex min-h-[40vh] items-center justify-center">
              <div className="h-9 w-9 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600" />
            </div>
          }
        >
          <SplashHandoff />
          <Routes>
            {/* Public Routes */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={<LandingGate />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/2fa-verify" element={<TwoFactorVerify />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route
                path="/terms-and-conditions"
                element={<TermsOfService />}
              />
              <Route path="/refund-policy" element={<RefundPolicy />} />
              <Route
                path="/privacy"
                element={<Navigate to="/privacy-policy" replace />}
              />
              <Route
                path="/terms"
                element={<Navigate to="/terms-and-conditions" replace />}
              />
              <Route
                path="/terms-of-service"
                element={<Navigate to="/terms-and-conditions" replace />}
              />
              <Route path="/blog" element={<BlogList />} />
              <Route path="/blog/:slug" element={<SinglePost />} />
              {/* Pricing is public so the paywall can link out to it. */}
              <Route path="/pricing" element={<Pricing />} />
              {/* Live Rx Portfolio — shareable public CV page. */}
              <Route path="/p/:slug" element={<PortfolioProfile />} />
            </Route>

            {/* PayHere return_url / cancel_url targets. */}
            <Route element={<ProtectedLayout />}>
              <Route path="/billing/return" element={<BillingReturn />} />
              <Route
                path="/billing/cancel"
                element={<BillingReturn canceled />}
              />
            </Route>

            <Route element={<ProtectedLayout />}>
              <Route
                path="/dashboard"
                element={
                  <AdminRoute>
                    <AdminDashboard />
                  </AdminRoute>
                }
              />
              <Route path="/editor" element={<CVBuilder />} />
              <Route path="/ai-cv-builder" element={<AICVBuilder />} />
              <Route path="/profile" element={<Profile />} />
            </Route>

            <Route
              path="/home"
              element={
                <ProtectedRoute>
                  <UserHome />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-resumes"
              element={
                <ProtectedRoute>
                  <MyResumes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ai-assistant"
              element={
                <ProtectedRoute>
                  <AIAssistant />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cover-letter"
              element={
                <ProtectedRoute>
                  <CoverLetter />
                </ProtectedRoute>
              }
            />
            <Route
              path="/templates"
              element={
                <ProtectedRoute>
                  <Templates />
                </ProtectedRoute>
              }
            />
            {/* Showcase CV — single parameterised route replaces 5 individual routes.
                Legacy paths (/devops, /flutter, etc.) now redirect to /showcase/:slug */}
            <Route path="/showcase/:slug" element={<ShowcaseCV />} />
            <Route path="/devops" element={<Navigate to="/showcase/devops" replace />} />
            <Route path="/flutter" element={<Navigate to="/showcase/flutter" replace />} />
            <Route path="/react" element={<Navigate to="/showcase/react" replace />} />
            <Route path="/software-engineer" element={<Navigate to="/showcase/software-engineer" replace />} />
            <Route path="/full-stack-developer" element={<Navigate to="/showcase/full-stack-developer" replace />} />
            <Route path="/templates/:key" element={<CVTemplatePreview />} />
            <Route path="/cv-builder" element={<CvBuilderAliasRedirect />} />
            {/* User Feedback Route */}
            <Route
              path="/feedback"
              element={
                <ProtectedRoute>
                  <Feedback />
                </ProtectedRoute>
              }
            />

            {/* Admin Feedback Management Route */}
            <Route
              path="/admin/feedback"
              element={
                <AdminRoute>
                  <FeedbackManagement />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/blog/new"
              element={
                <AdminRoute>
                  <CreatePost />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/blog/edit/:slug"
              element={
                <AdminRoute>
                  <EditPost />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/finance"
              element={
                <AdminRoute>
                  <FinancePage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/user-cvs"
              element={
                <AdminRoute>
                  <UserCvsPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/ai-logs"
              element={
                <AdminRoute>
                  <AiLogsPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/templates"
              element={
                <AdminRoute>
                  <TemplatesManagement />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/pricing"
              element={
                <AdminRoute>
                  <PricingManagement />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/newsletter"
              element={
                <AdminRoute>
                  <NewsletterPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/offers"
              element={
                <AdminRoute>
                  <OffersPage />
                </AdminRoute>
              }
            />
            <Route
              path="/newsletter/unsubscribe"
              element={<NewsletterUnsubscribe />}
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </MainLayout>
      <FeedbackPrompt />
    </div>
  );
}

export default App;
